import React, { useCallback, useEffect, useState } from 'react';
import {
  getUsers,
  createUser,
  updateUser,
  setUserPassword,
  getLocations,
  saveLocations,
  addLocation,
} from '../api';
import AppHeader from './AppHeader';
import {
  isAdmin,
  isParkLeadership,
  COMPANY_ROLES,
  SUPERVISOR_ASSIGNABLE_ROLES,
  PARK_LEADERSHIP_ASSIGNABLE_ROLES,
  roleLabel,
  canManageUserProfile,
} from '../utils/roles';

function Users({ user, onLogout, onNavigate }) {
  const isFullAdmin = isAdmin(user);
  const homeLocation = user?.location || 'Six Flags Saint Louis';
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [passwordModalValue, setPasswordModalValue] = useState('');
  const [newUser, setNewUser] = useState({
    id: '',
    name: '',
    email: '',
    password: '',
    department: 'Admissions',
    location: homeLocation,
    role: 'employee',
    active: true,
  });
  const [newLocationName, setNewLocationName] = useState('');

  const activeLocations = locations.filter((loc) => loc.active).map((loc) => loc.name);
  const assignableRoles = isFullAdmin
    ? COMPANY_ROLES
    : isParkLeadership(user)
      ? PARK_LEADERSHIP_ASSIGNABLE_ROLES
      : SUPERVISOR_ASSIGNABLE_ROLES;
  const visibleUsers = isFullAdmin
    ? users
    : users.filter((u) => u.location === homeLocation);

  const loadLocations = useCallback(async () => {
    try {
      const data = await getLocations();
      const list = data.locations || [];
      setLocations(list);
      const firstActive = list.find((loc) => loc.active)?.name || homeLocation;
      setNewUser((prev) => (prev.location ? prev : { ...prev, location: firstActive }));
    } catch (err) {
      setError(err.error || 'Unable to load locations');
    }
  }, [homeLocation]);

  const loadUsersList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getUsers();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.error || 'Unable to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLocations();
    loadUsersList();
  }, [loadLocations, loadUsersList]);

  const handleNewUserChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewUser((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleEditUserChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingUser((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const resetNewUserForm = () => {
    setNewUser({
      id: '',
      name: '',
      email: '',
      password: '',
      department: 'Admissions',
      location: homeLocation,
      role: 'employee',
      active: true,
    });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!newUser.id || !newUser.name || !newUser.email || !newUser.password) {
      setError('User ID, name, email, and password are required');
      return;
    }

    const payload = isFullAdmin
      ? { ...newUser, actorId: user?.id }
      : { ...newUser, location: homeLocation, actorId: user?.id };

    try {
      await createUser(payload);
      setMessage(`User ${newUser.id} created successfully`);
      resetNewUserForm();
      setShowAddUserModal(false);
      loadUsersList();
    } catch (err) {
      setError(err.error || 'Unable to create user');
    }
  };

  const handleSaveEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    if (!canManageUserProfile(user, editingUser)) {
      setError('You cannot edit this user profile');
      return;
    }
    setError('');
    setMessage('');

    try {
      const payload = {
        name: editingUser.name,
        email: editingUser.email,
        department: editingUser.department,
        location: isFullAdmin ? editingUser.location : homeLocation,
        role: editingUser.role,
        active: editingUser.active,
        actorId: user?.id,
      };
      await updateUser(editingUser.id, payload);
      setMessage(`User ${editingUser.id} updated`);
      setEditingUser(null);
      loadUsersList();
    } catch (err) {
      setError(err.error || 'Unable to update user');
    }
  };

  const openPasswordModal = (userItem) => {
    if (!canManageUserProfile(user, userItem)) {
      setError('You cannot change this user password');
      return;
    }
    setPasswordModalUser({ id: userItem.id, name: userItem.name });
    setPasswordModalValue('');
    setError('');
  };

  const closePasswordModal = () => {
    setPasswordModalUser(null);
    setPasswordModalValue('');
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    if (!passwordModalUser) return;
    if (!passwordModalValue.trim()) {
      setError('Password cannot be empty');
      return;
    }

    setError('');
    setMessage('');

    try {
      await setUserPassword(passwordModalUser.id, passwordModalValue, user?.id);
      setMessage(`Password updated for ${passwordModalUser.id}`);
      closePasswordModal();
    } catch (err) {
      setError(err.error || 'Unable to update password');
    }
  };

  const handleToggleLocationActive = async (locationName) => {
    const target = locations.find((loc) => loc.name === locationName);
    if (!target) return;
    const updated = locations.map((loc) =>
      loc.name === locationName ? { ...loc, active: !loc.active } : loc
    );
    setError('');
    setMessage('');
    try {
      await saveLocations(updated);
      setMessage(`"${locationName}" ${target.active ? 'deactivated' : 'activated'}.`);
      await loadLocations();
    } catch (err) {
      setError(err.error || 'Unable to update location');
    }
  };

  const handleAddLocation = async () => {
    const trimmed = newLocationName.trim();
    if (!trimmed) return;
    setError('');
    setMessage('');
    try {
      await addLocation(trimmed);
      setNewLocationName('');
      setMessage(`"${trimmed}" added.`);
      await loadLocations();
    } catch (err) {
      setError(err.error || 'Unable to add location');
    }
  };


  const userFormFields = (formUser, onChange, includePassword, idPrefix, options = {}) => {
    const { lockLocation = false, lockRoleAdmin = false } = options;
    const locationChoices = Array.from(
      new Set([
        ...(lockLocation ? [homeLocation] : activeLocations),
        formUser.location,
      ].filter(Boolean))
    );
    const roleChoices = lockRoleAdmin ? assignableRoles : COMPANY_ROLES;

    return (
      <>
        <div className="form-group">
          <label htmlFor={`${idPrefix}-id`}>User ID *</label>
          <input
            id={`${idPrefix}-id`}
            name="id"
            value={formUser.id}
            onChange={onChange}
            disabled={idPrefix === 'edit'}
            placeholder="USER004"
          />
        </div>
        <div className="form-group">
          <label htmlFor={`${idPrefix}-name`}>Name *</label>
          <input id={`${idPrefix}-name`} name="name" value={formUser.name} onChange={onChange} />
        </div>
        <div className="form-group">
          <label htmlFor={`${idPrefix}-email`}>Email *</label>
          <input
            id={`${idPrefix}-email`}
            name="email"
            type="email"
            value={formUser.email}
            onChange={onChange}
          />
        </div>
        {includePassword && idPrefix === 'new' && (
          <div className="form-group">
            <label htmlFor={`${idPrefix}-password`}>Password *</label>
            <input
              id={`${idPrefix}-password`}
              name="password"
              type="password"
              value={formUser.password || ''}
              onChange={onChange}
            />
          </div>
        )}
        <div className="form-group">
          <label htmlFor={`${idPrefix}-department`}>Department</label>
          <input
            id={`${idPrefix}-department`}
            name="department"
            value={formUser.department}
            onChange={onChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor={`${idPrefix}-location`}>Location</label>
          <select
            id={`${idPrefix}-location`}
            name="location"
            value={formUser.location}
            onChange={onChange}
            disabled={lockLocation}
          >
            {locationChoices.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor={`${idPrefix}-role`}>Role</label>
          <select id={`${idPrefix}-role`} name="role" value={formUser.role} onChange={onChange}>
            {roleChoices.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="active"
              checked={formUser.active}
              onChange={onChange}
            />{' '}
            Active employee
          </label>
        </div>
      </>
    );
  };

  return (
    <div className="container wide-container">
      <AppHeader
        user={user}
        pageTitle="Administration"
        subtitle={
          isFullAdmin
            ? 'Manage users, roles, and park locations.'
            : `Manage users at ${homeLocation} only.`
        }
        onNavigate={onNavigate}
        onLogout={onLogout}
        active="users"
      />

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}

      <div className="admin-tabs">
        <button
          type="button"
          className={activeTab === 'users' ? 'btn-primary' : 'btn-secondary'}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        {isFullAdmin && (
          <button
            type="button"
            className={activeTab === 'locations' ? 'btn-primary' : 'btn-secondary'}
            onClick={() => setActiveTab('locations')}
          >
            Locations
          </button>
        )}
      </div>

      {activeTab === 'users' && (
        <div className="search-results">
          <div className="section-header">
            <h2>{isFullAdmin ? 'All Users' : `Users at ${homeLocation}`}</h2>
            <button className="btn-primary" onClick={() => setShowAddUserModal(true)}>
              + Add New User
            </button>
          </div>
          {loading ? (
            <div className="no-results">Loading users…</div>
          ) : (
            <div className="table-scroll">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Department</th>
                    <th>Location</th>
                    <th>Role</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="subtitle" style={{ textAlign: 'center' }}>
                        No users found for this location.
                      </td>
                    </tr>
                  ) : (
                    visibleUsers.map((userItem) => (
                      <tr key={userItem.id}>
                        <td>{userItem.id}</td>
                        <td>{userItem.name}</td>
                        <td>{userItem.email}</td>
                        <td>{userItem.department}</td>
                        <td>{userItem.location}</td>
                        <td>{roleLabel(userItem.role)}</td>
                        <td>{userItem.active ? 'Yes' : 'No'}</td>
                        <td className="table-actions-cell">
                          <div className="table-actions-inner">
                            {canManageUserProfile(user, userItem) ? (
                              <>
                                <button
                                  type="button"
                                  className="btn-table"
                                  onClick={() => setEditingUser({ ...userItem })}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn-table btn-secondary-table"
                                  onClick={() => openPasswordModal(userItem)}
                                >
                                  Set Password
                                </button>
                              </>
                            ) : (
                              <span className="muted-small">View only</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'locations' && isFullAdmin && (
        <div className="search-results compensation-panel">
          <h2>Park Locations</h2>
          <p className="subtitle" style={{ marginBottom: '16px' }}>
            Locations are stored in SQLite. Inactive locations are hidden from
            user assignment and new submissions.
          </p>
          <table className="compensation-table">
            <thead>
              <tr>
                <th>Location</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {locations.length === 0 ? (
                <tr>
                  <td colSpan={3} className="subtitle" style={{ textAlign: 'center' }}>
                    No locations yet.
                  </td>
                </tr>
              ) : (
                locations.map((loc) => (
                  <tr
                    key={loc.name}
                    className={loc.active ? '' : 'compensation-row-inactive'}
                  >
                    <td>{loc.name}</td>
                    <td>{loc.active ? 'Yes' : 'No'}</td>
                    <td>
                      <button
                        type="button"
                        className={
                          loc.active ? 'btn-table btn-deactivate' : 'btn-table btn-activate'
                        }
                        onClick={() => handleToggleLocationActive(loc.name)}
                      >
                        {loc.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="compensation-toolbar" style={{ marginTop: '20px' }}>
            <div className="compensation-add-form">
              <input
                type="text"
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                placeholder="New park location name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddLocation();
                  }
                }}
              />
              <button type="button" className="btn-primary" onClick={handleAddLocation}>
                Add location
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddUserModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add a New User</h2>
              <button type="button" className="modal-close" onClick={() => setShowAddUserModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreateUser}>
              {userFormFields(newUser, handleNewUserChange, true, 'new', {
                lockLocation: !isFullAdmin,
                lockRoleAdmin: !isFullAdmin,
              })}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAddUserModal(false)}>
                  Cancel
                </button>
                <button type="submit">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit User {editingUser.id}</h2>
              <button type="button" className="modal-close" onClick={() => setEditingUser(null)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSaveEditUser}>
              {userFormFields(editingUser, handleEditUserChange, false, 'edit', {
                lockLocation: !isFullAdmin,
                lockRoleAdmin: !isFullAdmin,
              })}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setEditingUser(null)}>
                  Cancel
                </button>
                <button type="submit">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {passwordModalUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Set Password for {passwordModalUser.id}</h2>
              <button type="button" className="modal-close" onClick={closePasswordModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSavePassword}>
              <p className="subtitle" style={{ marginBottom: '16px' }}>
                Reset password for <strong>{passwordModalUser.name}</strong>
              </p>
              <div className="form-group">
                <label htmlFor="modal-password">New password *</label>
                <input
                  id="modal-password"
                  type="password"
                  value={passwordModalValue}
                  onChange={(e) => setPasswordModalValue(e.target.value)}
                  autoFocus
                  placeholder="Enter new password"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closePasswordModal}>
                  Cancel
                </button>
                <button type="submit">Save Password</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
